import { useState, useEffect, createContext, use } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Auth } from "../api";
import { hasExpiredToken } from "../utils";

const userController = new User();
const authController = new Auth();

export const AuthContext = createContext();

export function AuthProvider(props) {
  const { children } = props;
  // Inicializar el estado desde localStorage o usar el valor por defecto ADMIN
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("adminSidebarOpen");
    return savedState !== null ? JSON.parse(savedState) : true;
  });
  // Inicializar el estado desde localStorage o usar el valor por defecto STUDIENT
  const [studentSidebarOpen, setStudentSidebarOpen] = useState(() => {
    const savedStudentState = localStorage.getItem("studentSidebarOpen");
    return savedStudentState !== null ? JSON.parse(savedStudentState) : true;
  });
  // Inicializar el estado desde localStorage o usar el valor por defecto para el tema
  const [darkLightTheme, setDarkLightTheme] = useState(() => {
    const savedThemeState = localStorage.getItem("darkLightTheme");
    return savedThemeState !== null ? JSON.parse(savedThemeState) : false; // false para light mode por defecto
  });
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation(); // Hook para obtener la ruta actual

  // Actualizar localStorage cada vez que adminSidebarOpen y studentSidebarOpen cambie
  useEffect(() => {
    localStorage.setItem("adminSidebarOpen", JSON.stringify(adminSidebarOpen));
    localStorage.setItem(
      "studentSidebarOpen",
      JSON.stringify(studentSidebarOpen)
    );
  }, [adminSidebarOpen, studentSidebarOpen]);

  // Actualizar localStorage cada vez que darkLightTheme cambie
  useEffect(() => {
    localStorage.setItem("darkLightTheme", JSON.stringify(darkLightTheme));
  }, [darkLightTheme]);

  // Aplicar el tema al elemento document para que SweetAlert2 y otros elementos globales puedan acceder
  useEffect(() => {
    // eslint-disable-next-line no-undef
    if (typeof window !== "undefined" && window.document) {
      // eslint-disable-next-line no-undef
      window.document.documentElement.setAttribute(
        "data-theme",
        darkLightTheme ? "dark" : "light"
      );
    }
  }, [darkLightTheme]);

  useEffect(() => {
    (async () => {
      const accessToken = authController.getAccessToken();
      const refreshToken = authController.getRefreshToken();

      if (!accessToken || !refreshToken) {
        logout(false); // No redirigir - es un logout automático
        setLoading(false);
        return;
      }

      if (hasExpiredToken(accessToken)) {
        if (hasExpiredToken(refreshToken)) {
          logout(false); // No redirigir - es un logout automático por token expirado
        } else {
          await reLogin(refreshToken);
        }
      } else {
        await login(accessToken);
      }

      setLoading(false);
    })();
  }, []);

  // Escucha el evento global disparado por httpClient cuando una petición falla
  // por sesión vencida (token expirado/inválido y el refresh silencioso también falló).
  useEffect(() => {
    const handleSessionExpired = () => {
      logout(true);
    };
    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reLogin = async (refreshToken) => {
    try {
      console.log("Re-login");

      const refreshResponse = await authController.refreshAccessToken(refreshToken);
      const accessToken =
        refreshResponse?.accessToken ?? refreshResponse?.access ?? null;

      if (!accessToken) {
        logout(false);
        return;
      }

      authController.setAccessToken(accessToken);
      await login(accessToken);
    } catch (error) {
      console.error(error);
      logout(false);
    }
  };

  const login = async (accessToken) => {
    try {
      const response = await userController.getMe();
      // extraer permisos y roles
      if (location.pathname === "/login") {
        if (["66e2830fdeac1a8e3fc50d5d", 3].includes(response.roleId)) {
          navigate("/student/dashboard");
          setStudentSidebarOpen(true);
        } else {
          navigate("/admin/dashboard");
          setAdminSidebarOpen(true);
        }
      }
      delete response.password;
      setToken(accessToken);
      setUser(response);
    } catch (error) {
      console.error(error);
      // Si getMe falló por sesión vencida, httpClient ya intentó el refresh y,
      // si también falló, ya limpió los tokens y disparó "auth:session-expired"
      // (manejado arriba). Acá solo evitamos dejar el estado en memoria a medias.
      setUser(null);
      setToken(null);
    }
  };

  const logout = async (shouldRedirect = true) => {
    await authController.removeTokens();
    setUser(null);
    setToken(null);
    
    // Solo redirigir si es un logout explícito del usuario
    if (shouldRedirect) {
      navigate("/login");
    }
  };

  const data = {
    accessToken: token,
    user,
    login,
    logout,
    adminSidebarOpen,
    setAdminSidebarOpen,
    studentSidebarOpen,
    setStudentSidebarOpen,
    darkLightTheme,
    setDarkLightTheme,
  };

  if (loading) return null;

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
}
